/**
 * Channel catalogue presentation layer.
 *
 * The catalogue itself — titles, descriptions, requirements, pricing, icons,
 * cover images — comes from `/api/channels/definitions` (see
 * `useChannelDefinitions`). This module only adds the presentation concerns
 * the definitions don't carry: how channels are grouped on the page, the hero
 * gradient used as a fallback when a channel has no cover image, and which
 * enablement flow each channel uses.
 *
 * Only channels that exist as real nearst channel definitions are listed —
 * the Lovable design's instagram-shop / facebook-shop are deliberately
 * excluded.
 */

import { formatCurrency } from '@/lib/currency';

export const CHANNEL_GROUPS = [
	{
		key: 'discovery',
		eyebrow: 'Discovery',
		filterLabel: 'Search & Discovery',
		title: 'Discovery channels',
		description: 'Create frictionless online-to-offline discovery journeys for your local audience.',
		channelIds: ['google', 'local-ads']
	},
	{
		key: 'social',
		eyebrow: 'Social',
		filterLabel: 'Social Commerce',
		title: 'Social commerce',
		description: 'Get closer to your customers by offering new discovery and purchase experiences on social media platforms.',
		channelIds: ['meta-commerce', 'meta-commerce-feed', 'tiktok-shop-feed', 'pinterest-feed', 'snap-store-feed']
	},
	{
		key: 'website',
		eyebrow: 'Omnichannel',
		filterLabel: 'Website Tools',
		title: 'Website Tools',
		description: 'Leverage powerful web tools to create seamless customer experiences.',
		channelIds: ['storefront', 'product-reservations', 'local-checkout', 'search-widget']
	},
	{
		key: 'delivery',
		eyebrow: 'Delivery',
		filterLabel: 'Last Mile Delivery',
		title: 'Last Mile Delivery',
		description: 'Reach customers who want their order delivered the same day.',
		channelIds: ['uber-eats', 'deliveroo', 'just-eat']
	}
];

export const IN_SCOPE_CHANNEL_IDS = CHANNEL_GROUPS.flatMap((group) => group.channelIds);

// Hero gradient fallback per channel — rendered behind the icon when the
// definition carries no cover image. Ported from the Lovable design.
const HERO_GRADIENTS = {
	google: 'bg-gradient-to-br from-[hsl(217,89%,61%)] via-[hsl(142,71%,45%)] to-[hsl(45,100%,51%)]',
	'local-ads': 'bg-gradient-to-br from-[hsl(180,55%,75%)] to-[hsl(190,60%,65%)]',
	'meta-commerce': 'bg-gradient-to-br from-[hsl(270,80%,55%)] to-[hsl(250,85%,55%)]',
	'meta-commerce-feed': 'bg-gradient-to-br from-[hsl(220,90%,62%)] via-[hsl(215,85%,52%)] to-[hsl(230,80%,45%)]',
	'tiktok-shop-feed': 'bg-gradient-to-br from-[hsl(0,0%,12%)] via-[hsl(340,60%,18%)] to-[hsl(190,70%,22%)]',
	'pinterest-feed': 'bg-gradient-to-br from-[hsl(353,82%,58%)] via-[hsl(353,75%,48%)] to-[hsl(355,70%,38%)]',
	'snap-store-feed': 'bg-gradient-to-br from-[hsl(54,100%,65%)] via-[hsl(48,100%,55%)] to-[hsl(40,95%,48%)]',
	storefront: 'bg-gradient-unify',
	'product-reservations': 'bg-gradient-to-br from-[hsl(255,70%,55%)] to-[hsl(265,75%,50%)]',
	'local-checkout': 'bg-gradient-to-br from-[hsl(255,70%,55%)] to-[hsl(265,75%,50%)]',
	'search-widget': 'bg-gradient-to-br from-[hsl(210,80%,55%)] via-[hsl(225,75%,50%)] to-[hsl(245,70%,50%)]',
	'uber-eats': 'bg-gradient-to-br from-[hsl(0,0%,5%)] to-[hsl(0,0%,15%)]',
	deliveroo: 'bg-gradient-to-br from-[hsl(174,70%,55%)] to-[hsl(195,75%,55%)]',
	'just-eat': 'bg-gradient-to-br from-[hsl(20,90%,55%)] to-[hsl(30,95%,60%)]'
};

/**
 * Enablement flow per channel:
 *   - `enable`            channel goes live immediately via POST .../enable
 *   - `register-interest` channel goes `pending` via POST .../register-interest
 *                         — used for the manual-onboarding delivery channels
 *                         where CS needs to complete setup before the retailer
 *                         is live.
 *   - `wizard`            multi-step setup at /channels/{id}/setup before the
 *                         channel can flip to `enabled` — Meta OAuth + domain
 *                         verification, Product Reservations email capture +
 *                         NearSt handoff, Local Checkout Stripe Connect.
 *   - `ads-budget`        configured on the Ads page (budget per store)
 *   - `launch-storefront` the LaunchStorefrontModal celebration flow — the
 *                         modal fires the enable itself once the anticipation
 *                         phase completes (same flow the Channels empty state
 *                         and the Overview "unlock" card drive).
 *
 * NEA-6101: the 4 feed channels (meta-commerce-feed, tiktok-shop-feed,
 * pinterest-feed, snap-store-feed) moved from `register-interest` → `enable`.
 * They bill on click and there's no manual CS step to wait on, so the CTA
 * copy is "Enable" and the standard enable pipe fires (toggleStockChannels →
 * `shop.channel.enabled` SNS → `unify-billing/on-channel-enabled`, which is
 * the sole biller on this path since NEA-6421). The deferred billing + Slack
 * flow only makes sense for the delivery channels below, where CS needs to
 * finish setup before the retailer receives orders.
 *
 * Google Local Listings uses the plain `enable` flow — the GBP setup state
 * machine fires server-side off the resulting `shop.channel.enabled` event.
 */
const ENABLEMENT = {
	google: 'enable',
	'local-ads': 'ads-budget',
	'meta-commerce': 'wizard',
	'meta-commerce-feed': 'enable',
	'tiktok-shop-feed': 'enable',
	'pinterest-feed': 'enable',
	'snap-store-feed': 'enable',
	storefront: 'launch-storefront',
	'product-reservations': 'wizard',
	'local-checkout': 'wizard',
	'uber-eats': 'register-interest',
	deliveroo: 'register-interest',
	'just-eat': 'register-interest',
	'search-widget': 'enable'
};

/**
 * Channels with a non-Stripe price label (Local Ads is per-day, billed via
 * the InvoiceItem flow in NEA-5753). Everything else has its price resolved
 * from `entitlements.channels[id].prices` on the billing-status response —
 * see `pricingFromEntitlements` below.
 *
 * Local Ads is the one channel priced in the SHOP's currency rather than the
 * subscription's: `shop.currency` is what sets both the Google Ads account
 * currency and the daily Stripe InvoiceItem, so the label takes it as an
 * argument instead of reading the entitlements block.
 */
const PRICE_LABEL_OVERRIDES = {
	'local-ads': (shopCurrency) => `From ${formatCurrency(2, shopCurrency)} /day`
};

/**
 * Convert a Stripe price object (`{ amount, currency }`, amount in minor
 * units) to major units. Returns a `number`; the currency travels separately
 * as `priceCurrency` so the consumer can format it.
 */
const toMajorUnits = (price) => {
	if (price == null || typeof price.amount !== 'number') return null;
	return price.amount / 100;
};

/**
 * Derive the per-card pricing shape from the live entitlements block.
 * Returns the same keys the consumers (`ChannelCard`, `ChannelDetail`) read:
 *   { alwaysIncluded?, price?, priceLabel?, priceCurrency? }
 *
 * - `alwaysIncluded`: policy is `always-on` (Google, Storefront).
 * - `price`: default-price amount in major units (e.g. 10 for £10/mo).
 * - `priceLabel`: an override for non-monthly billing (Local Ads).
 * - `priceCurrency`: the currency the price is billed in — the Stripe price's
 *   own code (`gbp`, `usd`) for subscription add-ons, the shop's currency for
 *   Local Ads. Consumers must format with it; it is not always `gbp`.
 */
const pricingFromEntitlements = (id, entitlements, shopCurrency) => {
	if (PRICE_LABEL_OVERRIDES[id]) return { priceLabel: PRICE_LABEL_OVERRIDES[id](shopCurrency), priceCurrency: shopCurrency };
	const entry = entitlements?.channels?.[id];
	if (!entry) return {};
	if (entry.policy === 'always-on') return { alwaysIncluded: true };
	const major = toMajorUnits(entry.prices?.default);
	if (major == null) return {};
	return { price: major, priceCurrency: entry.prices?.default?.currency ?? 'gbp' };
};

/**
 * Channel titles reworded to match the latest Unify channel-page design
 * (NEA-5912). The shared `@nearst/channel-definitions` titles are authored for
 * the NearSt dashboard ("Storefront", "Product Reservations").
 */
const TITLES = {
	storefront: 'Local Storefront',
	'product-reservations': 'Reservations'
};

/**
 * Channel one-line descriptions, ported verbatim from the latest Unify design
 * (NEA-5912) for every in-scope channel, Britishised. Shown on the channel
 * cards and detail headers; overrides the NearSt-voiced definition description.
 */
const DESCRIPTIONS = {
	google: 'Drive organic growth by offering full visibility of your in-store product range across Google surfaces.',
	'local-ads': 'Boost your online presence and local SEO ranking by promoting products to relevant local customers.',
	'meta-commerce': 'Drive engagement on your Instagram and Facebook pages by enabling consumers to explore your product catalogue.',
	'meta-commerce-feed': 'List your products on Instagram and Facebook using a product feed, without the full Meta Commerce integration.',
	'tiktok-shop-feed': 'Reach new customers by showcasing your products on TikTok Shop.',
	'pinterest-feed': 'Inspire shoppers and drive sales by listing your products as shoppable Pins on Pinterest.',
	'snap-store-feed': 'Connect with a younger audience by showcasing your products on Snapchat.',
	storefront: 'Your Local Storefront — a searchable, SEO/AI-optimised page for showcasing your products and store information.',
	'product-reservations': 'Allow local customers to reserve products online, to pick-up and pay in store.',
	'local-checkout': 'Offer checkout functionality on top of your online discovery journeys.',
	'uber-eats':
		'Uber Eats delivers more than just food. List your products on Uber Eats and increase your sales by reaching additional local customers.',
	deliveroo:
		'Deliveroo delivers more than just food. List your products on Deliveroo and increase your sales by reaching additional local customers.',
	'just-eat':
		'Just Eat delivers more than just food. Create Just Eat menus for your store to reach customers looking for easy access to your products.',
	'search-widget': 'Add a searchable product widget to your own website powered by your Local Storefront inventory.'
};

/**
 * Re-voice shared `@nearst/channel-definitions` copy for Unify. The definitions
 * are authored for the NearSt dashboard and reference "NearSt" throughout every
 * channel's description and body copy; on the Unify channel pages that brand is
 * wrong (NEA-5912). We rebrand only at this presentation layer — the shared
 * definitions stay untouched so the NearSt retailers-dashboard keeps its own
 * wording. Channels needing more than a brand swap get an explicit override
 * above (`DESCRIPTIONS`) or below (`LONG_DESCRIPTIONS`).
 */
const unifyBrandCopy = (text) => (typeof text === 'string' ? text.replace(/NearSt/g, 'Unify') : text);

/**
 * Full channel-page body copy, ported verbatim from the latest Unify design
 * (the fresh-dashboard `channels.ts`) for every in-scope channel (NEA-5912),
 * Britishised. Same `## Overview / ## How it works / ## Requirements` markdown
 * shape `parseChannelSections` expects — overrides the NearSt-voiced shared
 * definition body. Any channel absent here falls back to the definition body,
 * brand-swapped via `unifyBrandCopy`.
 */
const LONG_DESCRIPTIONS = {
	google: `
## Overview
Google Local Listings put your live in-store stock in front of nearby shoppers the moment they search on Google. When someone looks up a product you carry, your store appears on Google Search, Maps, the Shopping tab and the Business Profile — with real-time availability, price and distance.

It's the simplest way to turn high-intent local searches like "running shoes near me" into in-store visits, with zero manual uploads.

## How it works
Unify syncs your full product catalogue and live stock levels to your Google Merchant Center account automatically. Every product is matched to your store location, kept up to date throughout the day, and surfaced on the Google surfaces shoppers already use.

When a local shopper searches for an item you stock, your store has a higher chance to show up in the local results with an "In stock nearby" tag, opening hours and directions — driving foot traffic straight to your door.

## Requirements
- Verified Google Business Profile for each physical store
- Store location in one of the supported countries
	`,
	'local-ads': `
## Overview
Local Ads increase the online visibility of your local products by showing relevant products at the top of the Google search results. This increases the chance of capturing local audiences from your competitors, converting online clicks into in-store sales.

## How it works
Unify automatically manages and optimises your product catalogue, account setup and targeting for your ads. You can configure a budget per store, and your ads will start showing up within 24 hours. The budget can be flexibly updated at any point to support seasonal campaigns or additional targeting.

When a shopper near your store searches for an item that you stock, your products will show at the top of the search results with a distance tag.

## Requirements
- At least 50 in-stock products
- Enable Google Local Listings
- Have physical store locations in one of the supported countries
	`,
	'meta-commerce': `
## Overview
Connecting your inventory to Meta Commerce will allow you to use a single product catalogue to run ads, tag products in posts on Facebook and Instagram and send products in WhatsApp Business conversations.

Make your products visible to an audience you've already built on social media and get new customers through Facebook's and Instagram's discovery features.

## How it works
Follow our guided step-by-step process to automatically set up a Meta Commerce account and start syncing your catalogue in less than 15 minutes.

After your catalogue is set up, all Facebook pages, Instagram profiles and WhatsApp Business accounts connected to your Meta Business Manager account will be able to use the local products in your catalogue in posts and messages.

We'll create a Meta Commerce catalogue for each of the stores on Unify, so that you can pick and choose products from the right stores for the related Facebook pages and Instagram profiles.

## Requirements
- At least 50 in-stock products, with product titles, images and descriptions
- Manager-level access to your Meta Business Manager account
- At least one Facebook page or Instagram profile for your stores
	`,
	'meta-commerce-feed': `
## Overview
Get your products listed on Instagram and Facebook via a product feed. This is a lightweight alternative to the full Meta Commerce integration — ideal if you want to manage your own Meta Commerce catalogue and simply need a feed URL.

## How it works
Unify generates a product feed from your live inventory that can be imported into your Meta Commerce Manager catalogue. Contact our support team to get set up.

## Requirements
- An active Unify inventory connection
- A Meta Business Manager account with at least one Facebook page or Instagram profile
- Product data (titles, descriptions, and images) for your inventory
- Product page links for each product — either from your own website or via your Local Storefront
	`,
	'tiktok-shop-feed': `
## Overview
Connect your product inventory to TikTok Shop and make your products discoverable to millions of TikTok users. Showcase your in-store products directly on one of the fastest-growing social commerce platforms.

## How it works
Unify generates a product feed from your live inventory that can be used to power your TikTok Shop catalogue. Contact our support team to get set up.

## Requirements
- An active Unify inventory connection
- A TikTok for Business account
- Product data (titles, descriptions, and images) for your inventory
- Product page links for each product — either from your own website or via your Local Storefront
	`,
	'pinterest-feed': `
## Overview
Connect your product inventory to Pinterest and turn your products into shoppable Pins. Help customers discover and buy your in-store products while they browse for inspiration.

## How it works
Unify generates a product feed from your live inventory that can be used to power your Pinterest product catalogue. Contact our support team to get set up.

## Requirements
- An active Unify inventory connection
- A Pinterest Business account
- Product data (titles, descriptions, and images) for your inventory
- Product page links for each product — either from your own website or via your Local Storefront
	`,
	'snap-store-feed': `
## Overview
Connect your product inventory to Snap Store and make your products available to Snapchat's engaged audience. Reach local shoppers through one of the most popular social platforms.

## How it works
Unify generates a product feed from your live inventory that can be used to power your Snap Store catalogue. Contact our support team to get set up.

## Requirements
- An active Unify inventory connection
- A Snapchat Business account
- Product data (titles, descriptions, and images) for your inventory
- Product page links for each product — either from your own website or via your Local Storefront
	`,
	storefront: `
## Overview
Your Local Storefront gives shoppers a polished, fully searchable, SEO/AI-optimised page for your store — all powered by your live inventory.

## How it works
Edit the storefront content from the Channels area: header image, bio, social media links, and your return and delivery policies. The storefront automatically lists your in-stock products and can be linked from feeds and ads.

## Requirements
- An active Unify inventory connection
	`,
	'product-reservations': `
## Overview
Add another touchpoint for local shoppers by offering the option to reserve online and pick-up in store when they are looking for nearby items. Unify creates product pages for all items, links them to channel listings, manages customer payments and order updates.

## How it works
Customers will land on your Unify hosted product pages from different channels, where they'll be offered the option to reserve a product.

You will receive automatic notifications via email as soon as a reservation request comes through. Order management is available in the dashboard, where you can mark orders as cancelled or completed and access customer notes.

## Requirements
- Have either Google Local Listings, Google Local Inventory Ads, or Meta Commerce enabled
- Have a product data source connected to show images and descriptions on your product pages
	`,
	'local-checkout': `
## Overview
Connect your Stripe account and offer your customers a way to buy online and pick up in store from all the channels showing your inventory. Unify creates product pages for all items, links them to channel listings, manages customer payments and order updates.

## How it works
Customers will land on your Unify hosted product pages from different channels, where they'll be offered a local-checkout buying option which accepts all modern payment methods, including Apple Pay and Google Pay.

Order management is available in the dashboard, where you can mark orders as cancelled or completed and access customer notes. Your store staff will be notified of new orders via email or SMS. Emails will be sent to your customers automatically when the status of an order changes.

## Requirements
- Have either Google Local Listings, Google Local Inventory Ads, or Meta Commerce enabled
- Have a product data source connected to show images and descriptions on your product pages
- Create or connect a Stripe merchant account to accept payments
	`,
	'uber-eats': `
## Overview
Connect your Uber Eats account and allow nearby customers to get products from your store delivered to them through the Uber Eats app.

## How it works
Unify automatically creates and manages the menu for your store.

The menu is updated daily with the product information you provide in the dashboard, combined with real-time product availability from your POS system. This ensures you always have up to date menus, significantly reducing order cancellations and refunds.

You can manage all product-specific settings, such as category, custom prices and restrictions from the dashboard.

## Requirements
- Have an active Uber Eats Merchant account
- Have a product data source connected to provide images and descriptions for your menu items
- If you are a convenience sector retailer, we can help you source the right data — contact us
	`,
	deliveroo: `
## Overview
Connect your Deliveroo account and allow nearby customers to get products from your store delivered to them through the Deliveroo app.

## How it works
Unify automatically creates and manages the menu for your store.

The menu is updated daily with the product information you provide in the dashboard, combined with real-time product availability from your POS system. This ensures you always have up to date menus, significantly reducing order cancellations and refunds.

You can manage all product-specific settings, such as category, custom prices and restrictions from the dashboard.

## Requirements
- Have an active Deliveroo partner account
- Have a product data source connected to provide images and descriptions for your menu items
- If you are a convenience sector retailer, we can help you source the right data — contact us
	`,
	'just-eat': `
## Overview
Export a feed containing inventory for Just Eat and allow nearby customers to get products from your store delivered to them through the Just Eat app.

## How it works
Unify will export your inventory, formatted as a menu and ready to send to Just Eat.

The menu export will have the latest product information you provide in the dashboard, combined with real-time product availability from your POS system.

You can manage all product-specific settings, such as category, custom prices and restrictions from the dashboard.

## Requirements
- Have an active Just Eat partner account
- Have a product data source connected to provide images and descriptions for your menu items
- If you are a convenience sector retailer, we can help you source the right data — contact us
	`,
	'search-widget': `
## Overview
Add a live product search box to your own website, powered by your Local Storefront inventory. Shoppers can search your in-stock products from anywhere on your site and click straight through to the product page on your Local Storefront.

## How it works
Enable the channel, copy the embed snippet from the install step, and paste it into your website's HTML. The widget is a small Web Component that renders inside its own shadow DOM — it won't inherit or leak your site's styles.

You can customise the primary colour, corner radius, font family and placeholder text without writing any code. Results and clicks are tracked in your Unify analytics.

## Requirements
- Local Storefront enabled on this shop (needed to serve the product pages the widget links to)
- A website you own where you can paste an HTML snippet
	`
};

/**
 * Unify-specific support links. The shared `@nearst/channel-definitions`
 * `helpArticleUrls` point at the legacy NearSt support site
 * (support.near.st); Unify has its own help centre (support.unify.near.st,
 * a HelpKit site with different article IDs), so we override per channel
 * here rather than editing the shared definitions. An entry wins over the
 * definition's `helpArticleUrls`; an empty array hides the Support block
 * (used where no Unify article exists yet). Channels absent from this map
 * fall back to the definition's own `helpArticleUrls`.
 */
const HELP_ARTICLE_URLS = {
	google: [
		{
			title: 'Unify and Google',
			url: 'https://support.unify.near.st/channels/gjPcV6kwfJExYDjVnhfkVA/unify-and-google/7MhDo5GbmbmCgtYgV8ZQAR'
		}
	],
	'local-ads': [
		{
			title: 'What are Local Inventory Ads?',
			url: 'https://support.unify.near.st/local-ads/8dmxNbogewaj5BNdm2kh5L/what-are-local-inventory-ads/5ivSyecHsT3J4Fugjw2K5c'
		}
	],
	storefront: [
		{
			title: 'Your Local Storefront & how to edit it',
			url: 'https://support.unify.near.st/channels/gjPcV6kwfJExYDjVnhfkVA/what-is-your-storefront-and-how-to-edit/7PYqmwag1LKt8xzHLpimqV'
		}
	],
	'product-reservations': [
		{
			title: 'What are reservations?',
			url: 'https://support.unify.near.st/orders-/7JASpDe6FvayPmBe32vX8m/what-are-reservations/7JASpDe6FxcEifMBSWP2wK'
		}
	],
	'local-checkout': [
		{
			title: 'What is Local Checkout?',
			url: 'https://support.unify.near.st/orders-/7JASpDe6FvayPmBe32vX8m/what-is-local-checkout/7LoHHWcPsBnZGEzMX98q2q'
		}
	],
	'search-widget': [
		{
			title: 'Install guide',
			url: 'https://support.unify.near.st/channels/gjPcV6kwfJExYDjVnhfkVA/adding-the-search-widget-to-your-website/tVfmh7RdtWmsmc63iKSjgg'
		}
	],
	// No Unify Meta Commerce article yet — hide rather than link to legacy NearSt support.
	'meta-commerce': []
};

export const getHelpArticleUrls = (id, definition) => HELP_ARTICLE_URLS[id] ?? definition?.helpArticleUrls;

export const getHeroGradient = (id) => HERO_GRADIENTS[id] ?? 'bg-gradient-unify';
export const getEnablement = (id) => ENABLEMENT[id] ?? 'enable';
export const getPricing = (id, entitlements, shopCurrency) => pricingFromEntitlements(id, entitlements, shopCurrency);
export const getTitle = (id, definition) => TITLES[id] ?? definition?.title;

/** The channel record for `id` from a `useChannels` list, or `null`. */
export const findChannelRecord = (channels, id) =>
	(Array.isArray(channels) ? channels.find((channel) => channel.type === id) : null) ?? null;

const recordStatus = (record) => {
	if (!record) return null;
	if (record.status) return record.status;
	if (record.enabled === true) return 'enabled';
	if (record.enabled === false) return 'disabled';
	return null;
};

/**
 * Every channel a retailer can switch off has a Channel settings sub-page at
 * `/channels/{id}/settings`, and that page owns its Disable action (NEA-6537).
 * The exclusions keep their own sidebar CTA instead: Google is set up and run
 * by the setup automation, Storefront has "Manage Local Storefront info" and
 * Local Ads has "Adjust budget" — matching the Lovable design.
 *
 * Shared so the manage page's settings pill and the settings page itself
 * can't disagree: a pill pointing at a channel the page 404s on would be
 * a dead end.
 */
const NO_SETTINGS_PAGE_CHANNEL_IDS = new Set(['google', 'local-ads', 'storefront']);

/** The order-channel pair, whose settings page also carries notification emails, SMS and email templates. */
const ORDER_CHANNEL_IDS = new Set(['product-reservations', 'local-checkout']);

export const isOrderChannel = (id) => ORDER_CHANNEL_IDS.has(id);

export const hasChannelSettingsPage = (id) => IN_SCOPE_CHANNEL_IDS.includes(id) && !NO_SETTINGS_PAGE_CHANNEL_IDS.has(id);

export const isChannelLive = (channels, id) => recordStatus(findChannelRecord(channels, id)) === 'enabled';
export const isChannelPending = (channels, id) => recordStatus(findChannelRecord(channels, id)) === 'pending';

/**
 * Channels provisioned automatically server-side, without a retailer-visible
 * enable/disable toggle. This is a UI concern — distinct from the matrix's
 * `policy: 'always-on'` (which only says "no Stripe item"). Google Local
 * Listings is set up by the Google setup automation; Storefront is
 * `policy: 'always-on'` for billing purposes but still requires an explicit
 * "launch storefront" action, so it's NOT here.
 *
 * Live-ness still comes from the channels table — the enabled `google` row
 * only exists once the setup automation has completed (Merchant Centre
 * account, store code, feeds). Until then the channel is `settingUp`, never
 * live: hard-coding it live told shops Google had never heard of that they
 * were "Connected · Live · N items syncing" (NEA-6120).
 */
const AUTO_PROVISIONED_CHANNEL_IDS = new Set(['google']);

/**
 * Merge a channel definition with its live status + entitlements pricing
 * into a single object the cards and sub-pages render from.
 *
 * `entitlements` comes from `useBillingStatus().billing.entitlements`. When
 * absent (still loading or in the no_subscription case before signup), the
 * `price` derivations fall back gracefully so the catalogue still renders.
 */
export const buildChannel = (id, definitions, channels, entitlements, shopCurrency) => {
	const definition = definitions?.[id];
	if (!definition) return null;
	const record = findChannelRecord(channels, id);
	const autoProvisioned = AUTO_PROVISIONED_CHANNEL_IDS.has(id);
	const status = recordStatus(record);
	return {
		...definition,
		...getPricing(id, entitlements, shopCurrency),
		id,
		record,
		status,
		live: status === 'enabled',
		pending: status === 'pending',
		settingUp: autoProvisioned && status !== 'enabled',
		autoProvisioned,
		title: TITLES[id] ?? definition.title,
		description: DESCRIPTIONS[id] ?? unifyBrandCopy(definition.description),
		longDescription: LONG_DESCRIPTIONS[id] ?? unifyBrandCopy(definition.longDescription),
		helpArticleUrls: getHelpArticleUrls(id, definition),
		enablement: getEnablement(id),
		heroGradient: getHeroGradient(id)
	};
};

/**
 * Build the grouped catalogue the Channels page renders. Groups with no
 * resolvable channels (definitions still loading) are dropped.
 */
export const buildCatalogue = (definitions, channels, entitlements, shopCurrency) =>
	CHANNEL_GROUPS.map((group) => ({
		...group,
		channels: group.channelIds.map((id) => buildChannel(id, definitions, channels, entitlements, shopCurrency)).filter(Boolean)
	})).filter((group) => group.channels.length > 0);

const stripInlineMarkdown = (text) => text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

/**
 * Split a definition's `longDescription` markdown into its heading sections.
 *
 * Channel definitions follow a fixed shape — Overview, How it works,
 * Requirements — so the detail page can render each section in its own block
 * without bundling a markdown renderer. Headings are written inconsistently
 * across definitions (some use `##`, some `###`), so any level of 2+ hashes
 * starts a new section. A section whose lines are all `- ` bullets is
 * returned as a `list`; everything else as `paragraphs`.
 */
export const parseChannelSections = (markdown) => {
	if (typeof markdown !== 'string' || !markdown.trim()) return [];
	return markdown
		.split(/\n#{2,}\s+/)
		.map((chunk) => chunk.trim())
		.filter(Boolean)
		.map((chunk) => {
			const newline = chunk.indexOf('\n');
			const heading = (newline === -1 ? chunk : chunk.slice(0, newline)).replace(/^#+\s*/, '').trim();
			const body = newline === -1 ? '' : chunk.slice(newline + 1).trim();
			const lines = body
				.split('\n')
				.map((line) => line.trim())
				.filter(Boolean);
			const isList = lines.length > 0 && lines.every((line) => line.startsWith('- '));
			return {
				heading,
				list: isList ? lines.map((line) => stripInlineMarkdown(line.slice(2).trim())) : null,
				paragraphs: isList
					? null
					: body
							.split(/\n{2,}/)
							.map((paragraph) => stripInlineMarkdown(paragraph.replace(/\s*\n\s*/g, ' ').trim()))
							.filter(Boolean)
			};
		});
};
